import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, File, Folder, Paperclip, Scissors, Square, X } from "lucide-react";
import { encodeTransferChunk } from "@racler/protocol";
import { resolveExecutionRoute } from "@racler/protocol";
import type { ContextItem, DoDItem, Effort, GitReadiness, GitStatusCheck, LastModel, ProviderInfo } from "@racler/protocol";
import { MAX_ATTACHMENT_BYTES } from "@racler/shared";
import { api } from "../lib/api.ts";
import { useRelay } from "../lib/relayContext.tsx";
import type { OpenFiles } from "../lib/useOpenFiles.ts";
import { ModelSelect } from "./ModelSelect.tsx";
import { GitSetupDialog } from "./GitSetupDialog.tsx";
import { WorkingTreePreflightDialog } from "./WorkingTreePreflightDialog.tsx";
import { gateAutonomous } from "../lib/gitSetup.ts";
import { useGitSetupApi } from "../lib/useGitSetupApi.ts";
import {
  MODES,
  isAgentUiMode,
  protocolModeOf,
  rememberMode,
  restoreMode,
  type UiMode,
  configuredProviders,
  effortsOf,
  initialSelection,
  isSendKey,
  isValidSelection,
  modelsOf,
  rememberSelection,
  withEffort,
  withModel,
  withProvider,
  type Selection,
} from "../lib/composer.ts";
import {
  findAtTrigger,
  insertAtMention,
  pruneMentionItems,
  addContextItem as appendContextItem,
  removeContextItem,
  navigateSuggestions,
  buildFileContextItem,
  buildFolderContextItem,
  buildSelectionContextItem,
  buildAttachmentContextItem,
} from "../lib/composerContext.ts";

interface Props {
  providers: ProviderInfo[] | null;
  scope: string;
  machineId?: string | undefined;
  projectId?: string | undefined;
  files?: OpenFiles | undefined;
  lastModel: LastModel | null;
  running: boolean;
  disabled?: boolean;
  onSend: (
    text: string,
    selection: Selection,
    contextItems?: ContextItem[],
    environmentType?: "host" | "worktree",
    environmentId?: string,
  ) => void;
  onStop: () => void;
  /** Called when the selection is resolved/changed (used for Retry). */
  onSelection: (s: Selection | null) => void;
}

const safeStorage = () => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
};

interface AtMatch {
  query: string;
  start: number;
  end: number;
}

interface SearchEntry {
  path: string;
  kind: "file" | "directory";
}

export function Composer({
  providers,
  scope,
  machineId,
  projectId,
  files,
  lastModel,
  running,
  disabled,
  onSend,
  onStop,
  onSelection,
}: Props) {
  const relay = useRelay();
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState<UiMode>(() => restoreMode(scope, safeStorage()));
  // Autonomous is only entered after the shared Git readiness gate; `pendingGit` holds the switch until then.
  const [pendingGit, setPendingGit] = useState<{ target: UiMode; readiness: GitReadiness } | null>(null);
  const [pendingPreflight, setPendingPreflight] = useState<{
    kind: "unsaved" | "dirty";
    changedCount?: number;
    untrackedCount?: number;
    modifiedCount?: number;
  } | null>(null);
  const gitApi = useGitSetupApi(machineId, projectId);
  const [dodItems, setDodItems] = useState<DoDItem[]>([]);
  const [selectedDodIds, setSelectedDodIds] = useState<Set<string>>(new Set());
  const [envLoading, setEnvLoading] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [contextItems, setContextItems] = useState<ContextItem[]>([]);
  // @file / @folder references live inline in the draft text; these back them for sending (not rendered as chips)
  const [mentionItems, setMentionItems] = useState<ContextItem[]>([]);
  const [atMatch, setAtMatch] = useState<AtMatch | null>(null);
  const [suggestions, setSuggestions] = useState<SearchEntry[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<{ transferId: string; name: string; progress: number }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  // Clear unsent context when Project changes
  useEffect(() => {
    setContextItems([]);
    setMentionItems([]);
    setAtMatch(null);
  }, [projectId]);

  // Drop mention items whose inline @reference was deleted from the draft
  useEffect(() => {
    setMentionItems((items) => pruneMentionItems(items, draft));
  }, [draft]);

  useEffect(() => {
    if (!machineId || !projectId || !isAgentUiMode(mode)) return;
    let cancelled = false;
    api
      .getDoD(machineId, projectId)
      .then((res) => {
        if (!cancelled) {
          setDodItems(res.items);
          const req = new Set(res.items.filter((i) => i.required).map((i) => i.id));
          setSelectedDodIds(req);
        }
      })
      .catch(() => {
        if (!cancelled) setDodItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [machineId, projectId, mode]);

  const usable = useMemo(() => configuredProviders(providers ?? []), [providers]);

  // Resolve the initial selection once the catalog is known, and repair it if the catalog changes under it.
  useEffect(() => {
    if (!providers) return;
    if (isValidSelection(usable, selection)) return;
    setSelection(initialSelection(providers, scope, lastModel));
  }, [providers, usable, selection, scope, lastModel]);
  useEffect(() => onSelection(selection), [selection, onSelection]);

  const choose = (next: Selection | null) => {
    setSelection(next);
    if (next) rememberSelection(scope, next);
  };

  const models = selection ? modelsOf(usable, selection.provider) : [];
  const efforts: Effort[] = effortsOf(usable, selection);

  const addContextItem = (item: ContextItem) => {
    setContextItems((existing) => appendContextItem(existing, item));
  };

  // Inspect textarea draft for @ query
  const checkAtTrigger = (text: string, cursorPos: number) => {
    const match = findAtTrigger(text, cursorPos);
    setAtMatch(match);
    if (match) {
      setHighlightedIndex(0);
    }
  };

  // Fetch file/folder suggestions when @ query is active
  const atQuery = atMatch?.query;
  useEffect(() => {
    if (atQuery === undefined || !machineId || !projectId) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    relay
      .machine(machineId, "filesystem/searchEntries", {
        projectId,
        query: atQuery,
        limit: 50,
      })
      .then((res) => {
        if (!cancelled) {
          setSuggestions(res.entries);
          setHighlightedIndex(0);
        }
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [atQuery, machineId, projectId, relay]);

  const selectSuggestion = (entry: SearchEntry) => {
    if (!projectId || !atMatch) return;
    const item: ContextItem =
      entry.kind === "directory"
        ? buildFolderContextItem(projectId, entry.path)
        : buildFileContextItem(projectId, entry.path);
    setMentionItems((existing) => appendContextItem(existing, item));

    // Keep the reference inline at the position where it was inserted (no separate chip)
    const { newText, cursor } = insertAtMention(draft, atMatch, entry.path);
    setDraft(newText);
    setAtMatch(null);
    const el = area.current;
    if (el) {
      el.focus();
      requestAnimationFrame(() => el.setSelectionRange(cursor, cursor));
    }
  };

  const handleUploadFiles = async (filesToUpload: File[]) => {
    if (!machineId || !projectId) return;
    for (const file of filesToUpload) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        alert(`File "${file.name}" exceeds the maximum attachment size limit of 10MB.`);
        continue;
      }
      const transferId = crypto.randomUUID();
      setUploadingFiles((prev) => [...prev, { transferId, name: file.name, progress: 0 }]);
      try {
        await relay.machine(machineId, "transfer/transfer.start", {
          transferId,
          projectId,
          targetKind: "prompt_attachment",
          destinationDir: "",
          fileName: file.name,
          size: file.size,
          mimeType: file.type || undefined,
        });

        const chunkSize = 256 * 1024;
        let offset = 0;
        while (offset < file.size) {
          const slice = file.slice(offset, offset + chunkSize);
          const buffer = await slice.arrayBuffer();
          const framed = encodeTransferChunk(transferId, offset, new Uint8Array(buffer));
          relay.sendBinary(framed);
          offset += buffer.byteLength;
          const currentProgress = Math.round((offset / file.size) * 100);
          setUploadingFiles((prev) =>
            prev.map((u) => (u.transferId === transferId ? { ...u, progress: currentProgress } : u)),
          );
          await relay.waitForDrain();
        }

        const res = (await relay.machine(machineId, "transfer/transfer.complete", {
          transferId,
          expectedSize: file.size,
        })) as { attachmentId?: string; kind?: "text" | "image" | "pdf"; name: string; size: number };

        const kind = res.kind ?? "text";
        const item = buildAttachmentContextItem(projectId, {
          attachmentId: res.attachmentId ?? transferId,
          name: file.name,
          mediaType: file.type || "application/octet-stream",
          size: file.size,
          kind,
        });
        addContextItem(item);
      } catch (err) {
        console.error("Failed to upload prompt attachment:", err);
        alert(`Failed to attach "${file.name}": ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setUploadingFiles((prev) => prev.filter((u) => u.transferId !== transferId));
      }
    }
  };

  const handleImportAttachment = async (item: ContextItem) => {
    if (item.type !== "attachment" || !machineId || !projectId) return;
    try {
      const res = await relay.machine(machineId, "transfer/attachment.import", {
        projectId,
        attachmentId: item.attachmentId,
        destinationDir: "",
        collisionPolicy: "rename",
      });

      setContextItems((items) =>
        items.map((it) =>
          it.type === "attachment" && it.attachmentId === item.attachmentId
            ? buildFileContextItem(projectId, res.path)
            : it,
        ),
      );
    } catch (err) {
      console.error("Failed to import attachment into project:", err);
      alert(`Failed to import attachment: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleRemoveItem = (item: ContextItem, idx: number) => {
    setContextItems((items) => removeContextItem(items, idx));
    if (item.type === "attachment" && machineId && projectId) {
      relay
        .machine(machineId, "transfer/attachment.delete", {
          projectId,
          attachmentId: item.attachmentId,
        })
        .catch(() => undefined);
    }
  };

  const submit = async (gateChecked = false) => {
    const text = draft.trim();
    if (!text || !selection || running || disabled || envLoading) return;
    const itemsToSend = pruneMentionItems(mentionItems, text).reduce(
      (acc, item) => appendContextItem(acc, item),
      [...contextItems],
    );

    // Where this run executes is decided centrally (Chat/Interactive: Host, Autonomous: Worktree).
    const proto = protocolModeOf(mode);
    const route = resolveExecutionRoute(proto);

    let envId: string | undefined;
    if (route.environment === "worktree") {
      if (!machineId || !projectId || !gitApi) return;

      // PREFLIGHT 1: Check unsaved Editor buffers
      if (files && files.dirtyPaths().length > 0) {
        setPendingPreflight({ kind: "unsaved" });
        return;
      }

      // PREFLIGHT 2: Check Git setup readiness (installed, is repo, has HEAD)
      if (!gateChecked) {
        const gate = await gateAutonomous(gitApi);
        if (gate.kind === "setup") {
          setPendingGit({ target: "autonomous", readiness: gate.readiness });
          return; // never falls back to the Host
        }
      }

      // PREFLIGHT 3: Check Git working-tree state
      let gitStatus: GitStatusCheck;
      try {
        gitStatus = gitApi.status ? await gitApi.status() : await api.gitStatus(machineId, projectId);
      } catch (err) {
        alert(`Git status check failed: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
      if (!gitStatus.isClean) {
        setPendingPreflight({
          kind: "dirty",
          changedCount: gitStatus.changedCount ?? gitStatus.changedFiles?.length ?? 1,
          untrackedCount: gitStatus.untrackedCount,
          modifiedCount: gitStatus.modifiedCount,
        });
        return;
      }

      setEnvLoading(true);
      try {
        envId = (await api.createEnvironment(machineId, projectId, "worktree")).environment.id;
      } catch (err) {
        alert(`Agent Autonomous could not start. Nothing was changed in your project. ${err instanceof Error ? err.message : String(err)}`);
        return;
      } finally {
        setEnvLoading(false);
      }
    }

    setDraft("");
    setContextItems([]);
    setMentionItems([]);
    setAtMatch(null);
    onSend(
      text,
      {
        ...selection,
        ...proto,
        ...(isAgentUiMode(mode)
          ? {
              strictReview: mode === "interactive" ? true : false,
              dod: dodItems.map((d) => ({ ...d, required: selectedDodIds.has(d.id) })),
            }
          : {}),
      },
      itemsToSend,
      isAgentUiMode(mode) ? route.environment : undefined,
      envId,
    );
  };

  // Mode switching: Autonomous is gated by the shared Git readiness check; anything not READY keeps the current mode.
  const changeMode = async (next: UiMode) => {
    if (next === mode) return;
    if (next === "autonomous") {
      if (!gitApi) return;
      const gate = await gateAutonomous(gitApi);
      if (gate.kind === "setup") {
        setPendingGit({ target: next, readiness: gate.readiness });
        return;
      }
    }
    setMode(next);
    rememberMode(scope, next, safeStorage());
  };

  const selectedProvider = usable.find((p) => p.id === selection?.provider);
  const selectedModelDesc = selectedProvider?.models.find((m) => m.id === selection?.model);

  const unsupportedAttachments = contextItems.filter((item) => {
    if (item.type !== "attachment") return false;
    if (item.kind === "image" && !selectedModelDesc?.supportsImageInput) return true;
    if (item.kind === "pdf" && !selectedModelDesc?.supportsPdfInput) return true;
    return false;
  });
  const hasUnsupportedAttachments = unsupportedAttachments.length > 0;

  const noModels = providers !== null && usable.every((p) => p.models.length === 0);
  const canSend =
    !!draft.trim() &&
    !!selection &&
    !disabled &&
    uploadingFiles.length === 0 &&
    !hasUnsupportedAttachments;

  const activeSelection = files?.selection ?? null;

  return (
    <form
      className={`composer mode-${mode} ${isDragging ? "is-dragging" : ""}`}
      data-mode={mode}
      data-testid="composer"
      style={{ position: "relative" }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          void handleUploadFiles(Array.from(e.dataTransfer.files));
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {/* Context chips */}
      {(contextItems.length > 0 || uploadingFiles.length > 0) && (
        <div className="composer-chips" data-testid="composer-context-chips">
          {contextItems.map((item, idx) => {
            const isUnsupported =
              item.type === "attachment" &&
              ((item.kind === "image" && !selectedModelDesc?.supportsImageInput) ||
                (item.kind === "pdf" && !selectedModelDesc?.supportsPdfInput));
            return (
              <span key={idx} className="composer-chip" data-testid={`chip-${item.type}`}>
                <span className="chip-badge">
                  [{item.type === "project_file" ? "file" : item.type === "project_folder" ? "folder" : item.type === "editor_selection" ? "selection" : "attachment"}]
                </span>
                <span className="chip-label">
                  {item.type === "attachment" ? item.name : item.path}
                  {item.type === "editor_selection" ? `:${item.startLine}-${item.endLine}` : ""}
                </span>
                {isUnsupported && (
                  <span className="chip-warning" data-testid="chip-unsupported-warning">
                    (unsupported by model)
                  </span>
                )}
                {item.type === "attachment" && (
                  <button
                    type="button"
                    className="chip-action-btn"
                    title="Import into Project"
                    data-testid={`import-attachment-${idx}`}
                    onClick={() => void handleImportAttachment(item)}
                  >
                    Import
                  </button>
                )}
                <button
                  type="button"
                  className="chip-remove"
                  aria-label={`Remove ${item.type === "attachment" ? item.name : item.path}`}
                  data-testid={`remove-chip-${idx}`}
                  onClick={() => handleRemoveItem(item, idx)}
                >
                  <X size={12} aria-hidden />
                </button>
              </span>
            );
          })}
          {uploadingFiles.map((u) => (
            <span key={u.transferId} className="composer-chip uploading" data-testid="chip-uploading">
              <span className="chip-badge">[uploading]</span>
              <span className="chip-label">{u.name} ({u.progress}%)</span>
            </span>
          ))}
        </div>
      )}

      {/* Autocomplete popover */}
      {atMatch && (
        <div className="composer-at-popover" data-testid="at-autocomplete-popover">
          {suggestions.length === 0 ? (
            <div className="composer-at-empty muted small">No files or folders found</div>
          ) : (
            suggestions.map((s, idx) => (
              <button
                key={s.path}
                type="button"
                className={`composer-at-item ${idx === highlightedIndex ? "active" : ""}`}
                data-testid={`at-item-${idx}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(s);
                }}
              >
                {s.kind === "directory" ? (
                  <Folder size={13} className="at-icon folder" aria-hidden />
                ) : (
                  <File size={13} className="at-icon file" aria-hidden />
                )}
                <span className="at-path">{s.path}</span>
              </button>
            ))
          )}
        </div>
      )}

      <textarea
        ref={area}
        data-testid="chat-input"
        placeholder={MODES.find((m) => m.id === mode)?.placeholder}
        aria-label="Message"
        value={draft}
        rows={3}
        maxLength={20000}
        onChange={(e) => {
          const val = e.target.value;
          setDraft(val);
          checkAtTrigger(val, e.target.selectionStart);
        }}
        onKeyUp={(e) => {
          checkAtTrigger(draft, e.currentTarget.selectionStart);
        }}
        onKeyDown={(e) => {
          if (atMatch && suggestions.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightedIndex((i) => navigateSuggestions(i, suggestions.length, "down"));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightedIndex((i) => navigateSuggestions(i, suggestions.length, "up"));
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              if (suggestions[highlightedIndex]) {
                selectSuggestion(suggestions[highlightedIndex]);
              }
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setAtMatch(null);
              return;
            }
          }
          if (isSendKey(e)) {
            e.preventDefault();
            void submit();
          }
        }}
      />
      <div className="composer-bar">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          data-testid="attachment-input"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              void handleUploadFiles(Array.from(e.target.files));
              e.target.value = "";
            }
          }}
        />
        <button
          type="button"
          className="icon-btn"
          disabled={!machineId || !projectId || running}
          title="Attach files to prompt"
          aria-label="Attach files"
          data-testid="attach-file-btn"
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip size={15} strokeWidth={1.75} aria-hidden />
        </button>

        {/* Explicit context affordances */}
        {activeSelection && projectId && (
          <button
            type="button"
            className="composer-action-btn"
            data-testid="add-current-selection"
            title={`Add selection (${activeSelection.path}:${activeSelection.startLine}-${activeSelection.endLine})`}
            onClick={() => {
              addContextItem(buildSelectionContextItem(projectId, activeSelection));
            }}
          >
            <Scissors size={13} aria-hidden />
            <span>+ Selection</span>
          </button>
        )}

        <select
          aria-label="Mode"
          data-testid="mode"
          value={mode}
          disabled={running}
          onChange={(e) => void changeMode(e.target.value as UiMode)}
        >
          {MODES.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Provider"
          data-testid="provider-select"
          disabled={running || usable.length === 0}
          value={selection?.provider ?? ""}
          onChange={(e) => choose(withProvider(usable, e.target.value))}
        >
          {usable.length === 0 && <option value="">No provider</option>}
          {usable.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <ModelSelect
          models={models}
          value={selection?.model ?? ""}
          disabled={running}
          emptyLabel={noModels ? "No models available" : undefined}
          providerName={usable.find((p) => p.id === selection?.provider)?.name}
          onChange={(id) => selection && choose(withModel(usable, selection, id))}
        />
        {efforts.length > 0 && (
          <select
            aria-label="Reasoning effort"
            data-testid="effort-select"
            disabled={running}
            value={selection?.effort ?? ""}
            onChange={(e) =>
              selection && choose(withEffort(selection, (e.target.value || undefined) as Effort | undefined))
            }
          >
            <option value="">Effort: default</option>
            {efforts.map((x) => (
              <option key={x} value={x}>
                Effort: {x}
              </option>
            ))}
          </select>
        )}
        <span className="spacer" />
        {running ? (
          <button
            type="button"
            className="send-btn stop"
            onClick={onStop}
            title="Stop"
            data-testid="chat-stop"
          >
            <Square size={13} fill="currentColor" aria-hidden />
            Stop
          </button>
        ) : (
          <button
            type="submit"
            className="send-btn"
            disabled={!canSend}
            title="Send (Enter)"
            aria-label="Send"
            data-testid="chat-send"
          >
            <ArrowUp size={15} strokeWidth={2.25} aria-hidden />
          </button>
        )}
      </div>
      {pendingGit && gitApi && (
        <GitSetupDialog
          api={gitApi}
          initial={pendingGit.readiness}
          onClose={() => setPendingGit(null)} // cancel: the previous mode (and the draft) stay untouched
          onReady={() => {
            const target = pendingGit.target;
            setPendingGit(null);
            setMode(target);
            rememberMode(scope, target, safeStorage());
          }}
        />
      )}
      {pendingPreflight && (
        <WorkingTreePreflightDialog
          kind={pendingPreflight.kind}
          changedCount={pendingPreflight.changedCount}
          untrackedCount={pendingPreflight.untrackedCount}
          modifiedCount={pendingPreflight.modifiedCount}
          gitApi={gitApi}
          hasUnsavedBuffers={() => (files ? files.dirtyPaths().length > 0 : false)}
          onSaveAllAndContinue={async () => {
            if (!files) return;
            await files.saveAll();
            const failed = files.files.some((f) => f.dirty || f.error);
            if (failed) {
              const errMsg = files.files.find((f) => f.error)?.error ?? "Some files could not be saved.";
              throw new Error(errMsg);
            }
            setPendingPreflight(null);
            // Automatically rerun preflight with draft preserved
            void submit();
          }}
          onCommitAndStart={async (message, identity) => {
            if (!gitApi?.commit) throw new Error("Git commit API not available.");
            const res = await gitApi.commit({ message, identity });
            if (res.needsIdentity) {
              return false;
            }
            if (!res.ok) {
              throw new Error(res.error ?? "Git commit failed.");
            }
            // 1. rerun Git working-tree status
            const gitStatus = gitApi.status ? await gitApi.status() : await api.gitStatus(machineId!, projectId!);
            if (!gitStatus.isClean) {
              throw new Error(gitStatus.reason ?? "Project working tree is still dirty after commit.");
            }
            // 2. rerun existing Git readiness
            const gate = await gateAutonomous(gitApi);
            if (gate.kind !== "ready") {
              throw new Error(gate.readiness.message ?? "Project is not ready for Autonomous after commit.");
            }
            // 3. require clean + READY state: satisfied
            // 4. close all preflight/review dialogs
            setPendingPreflight(null);
            // 5. automatically start the ORIGINAL Autonomous prompt
            void submit();
            return true;
          }}
          onDiscardAndStart={async () => {
            if (files && files.dirtyPaths().length > 0) {
              throw new Error("You have unsaved files. Save or close them before discarding local changes.");
            }
            if (!gitApi?.discard) throw new Error("Git discard API not available.");
            const res = await gitApi.discard();
            if (!res.ok) {
              throw new Error(res.error ?? "Git discard failed.");
            }
            // 1. rerun Git working-tree status
            const gitStatus = gitApi.status ? await gitApi.status() : await api.gitStatus(machineId!, projectId!);
            if (!gitStatus.isClean) {
              throw new Error(gitStatus.reason ?? "Project working tree is still dirty after discard.");
            }
            // 2. rerun existing Git readiness
            const gate = await gateAutonomous(gitApi);
            if (gate.kind !== "ready") {
              throw new Error(gate.readiness.message ?? "Project is not ready for Autonomous after discard.");
            }
            // 3. require clean + READY state: satisfied
            // 4. close all preflight/review dialogs
            setPendingPreflight(null);
            // 5. automatically start the ORIGINAL Autonomous prompt
            void submit();
            return true;
          }}
          onClose={() => setPendingPreflight(null)}
        />
      )}

    </form>
  );
}
